// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import Users from '@app/features/user/state/Users';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

export interface ClickUpNotification {
	id: string;
	task_id: string;
	task_name: string;
	list_name: string;
	list_id?: string | null;
	description: string;
	/** A comment's pictures, through the media proxy; absent from older servers. */
	pictures?: Array<ClickUpInboxPicture>;
	created_at: string;
	is_read: boolean;
	is_cleared: boolean;
}
export interface ClickUpInboxPicture {
	name: string;
	width: number | null;
	height: number | null;
	/** A small preview. */
	thumbnail: string;
	/** The whole picture. */
	url: string;
}
interface InboxPage {
	enabled: boolean;
	items: Array<ClickUpNotification>;
	next_cursor: string | null;
}
/** KONVO_CLICKUP_INBOX_UPDATE: new or changed items, or everything up to an id cleared elsewhere. */
export type ClickUpInboxUpdate = {items: Array<ClickUpNotification>} | {cleared_through: string};

/** Waits between quiet retries after a background load fails. */
const RETRY_DELAYS_MS = [10_000, 30_000, 60_000, 120_000, 300_000];
/** Background failures in a row before the inbox says it could not refresh. */
const FAILURES_BEFORE_NOTICE = 3;

/** Ids start with the zero-padded time, so plain string order is time order. */
const newestFirst = (a: ClickUpNotification, b: ClickUpNotification) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

/**
 * The person's ClickUp activity. Live: the server pushes every new item and read/clear change
 * (KONVO_CLICKUP_INBOX_UPDATE), so the open inbox loads once and again after the gateway
 * reconnects (events can be missed while offline). Cleared items load only when the Cleared
 * tab asks for them.
 */
export class KonvoClickUpInboxStore {
	items: Array<ClickUpNotification> = [];
	enabled: boolean | null = null;
	loaded = false;
	loading = false;
	error = false;
	mutationError = false;
	clearingAll = false;
	pending = new Set<string>();
	/** Cleared items load page by page: undefined until asked for, null once all are here. */
	clearedCursor: string | null | undefined = undefined;
	clearedLoading = false;
	private generation = 0;
	private revision = 0;
	private consumers = 0;
	private cleanup: (() => void) | null = null;
	private failures = 0;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	/** A load was missed (reconnect while hidden or busy, or a failure): load again when possible. */
	private stale = false;
	/** Whose items these are: they stay between visits (Chats shows them at once) for the same person only. */
	private ownerId: string | null = null;

	constructor() {
		makeAutoObservable<this, 'cleanup' | 'failures' | 'retryTimer' | 'stale' | 'ownerId'>(
			this,
			{cleanup: false, failures: false, retryTimer: false, stale: false, ownerId: false},
			{autoBind: true},
		);
	}
	get unreadCount(): number {
		return this.items.filter((item) => !item.is_read && !item.is_cleared).length;
	}
	get activeItems(): Array<ClickUpNotification> {
		return this.items.filter((item) => !item.is_cleared);
	}
	/** More cleared items wait on the server. */
	get moreCleared(): boolean {
		return this.clearedCursor !== null;
	}

	/** Share one load across every mounted entry point; discard responses from a previous account. */
	subscribe(): () => void {
		this.consumers += 1;
		if (this.consumers === 1) {
			this.cleanup = reaction(
				() => Users.currentUser?.id,
				(userId) => {
					// Another account (or signed out): drop the last person's items. The same person
					// coming back keeps what was loaded; live updates kept it current meanwhile.
					if ((userId ?? null) !== this.ownerId) {
						this.reset();
						this.ownerId = userId ?? null;
					}
					if (!this.loaded || this.stale) void this.refresh();
				},
				{fireImmediately: true},
			);
		}
		return () => {
			this.consumers -= 1;
			if (this.consumers === 0) {
				this.cleanup?.();
				this.cleanup = null;
				this.clearRetry();
				// Keep the items for the next visit; only a request in flight is forgotten.
				runInAction(() => {
					this.generation += 1;
					this.loading = false;
					this.clearedLoading = false;
				});
			}
		};
	}
	/** READY or RESUMED: whatever arrived while the socket was down was not pushed. */
	handleReconnect(): void {
		// A load already out (the first one at startup) reads the server as it is now.
		if (this.loading) return;
		if (this.consumers > 0) void this.refresh();
		else if (this.loaded) this.stale = true;
	}
	/** A live update from the server, for this person's own inbox. */
	applyUpdate(update: ClickUpInboxUpdate): void {
		if (!this.loaded || this.ownerId !== (Users.currentUser?.id ?? null)) return;
		this.revision += 1;
		if ('items' in update) {
			const byId = new Map(update.items.map((item) => [item.id, item]));
			// A cleared item only joins the list when the Cleared tab already loaded that far.
			const cutoff = this.clearedCursor;
			const kept = this.items.filter((item) => !byId.has(item.id));
			const added = update.items.filter(
				(item) =>
					!item.is_cleared ||
					this.items.some((known) => known.id === item.id) ||
					(cutoff !== undefined && (cutoff === null || item.id > cutoff)),
			);
			this.items = [...kept, ...added].sort(newestFirst);
		} else {
			const through = update.cleared_through;
			this.items = this.items.map((item) =>
				item.is_cleared || item.id > through ? item : {...item, is_read: true, is_cleared: true},
			);
		}
	}
	reset(): void {
		this.generation += 1;
		this.clearRetry();
		this.failures = 0;
		this.stale = false;
		this.items = [];
		this.enabled = null;
		this.loaded = false;
		this.loading = false;
		this.error = false;
		this.mutationError = false;
		this.clearingAll = false;
		this.clearedCursor = undefined;
		this.clearedLoading = false;
		this.pending.clear();
	}
	/**
	 * Load the open inbox. A background load that fails retries quietly while the list is
	 * showing; `manual` (the refresh button, Retry) reports a failure at once.
	 */
	async refresh({manual = false}: {manual?: boolean} = {}): Promise<void> {
		if (!Users.currentUser?.id) return;
		if (this.loading || this.pending.size || this.clearingAll) {
			// Busy: load again once the request or save in flight is done.
			this.stale = true;
			return;
		}
		this.clearRetry();
		this.stale = false;
		const generation = this.generation;
		const revision = this.revision;
		this.loading = true;
		let again = false;
		try {
			const active: Array<ClickUpNotification> = [];
			let cursor: string | null = null;
			let enabled = false;
			do {
				const response: {body: InboxPage} = await http.get<InboxPage>(
					`${Endpoints.KONVO_CLICKUP_INBOX}?state=active${cursor ? `&before=${encodeURIComponent(cursor)}` : ''}`,
				);
				if (generation !== this.generation) return;
				active.push(...response.body.items);
				enabled = response.body.enabled;
				cursor = response.body.next_cursor;
			} while (cursor);
			runInAction(() => {
				if (revision === this.revision) {
					const activeIds = new Set(active.map((item) => item.id));
					// Gone from the open inbox means cleared elsewhere (items are never deleted).
					const cleared = this.items
						.filter((item) => !activeIds.has(item.id))
						.map((item) => (item.is_cleared ? item : {...item, is_cleared: true}));
					this.items = [...active, ...cleared].sort(newestFirst);
				}
				// A change landed or a reconnect came while this load was out: load again so none is lost.
				again = revision !== this.revision || this.stale;
				this.enabled = enabled;
				this.loaded = true;
				this.error = false;
				this.failures = 0;
			});
		} catch {
			if (generation === this.generation)
				runInAction(() => {
					this.failures += 1;
					this.stale = true;
					this.error = manual || !this.loaded || this.failures >= FAILURES_BEFORE_NOTICE;
					if (this.consumers > 0) this.scheduleRetry();
				});
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.loading = false;
				});
		}
		if (again && generation === this.generation) void this.refresh();
	}
	/** Load cleared items until at least `count` are here or there are no more. */
	async loadCleared(count: number): Promise<void> {
		if (this.clearedLoading || !Users.currentUser?.id) return;
		const generation = this.generation;
		this.clearedLoading = true;
		try {
			while (this.clearedCursor !== null && this.items.filter((item) => item.is_cleared).length < count) {
				const cursor = this.clearedCursor;
				const response: {body: InboxPage} = await http.get<InboxPage>(
					`${Endpoints.KONVO_CLICKUP_INBOX}?state=cleared${cursor ? `&before=${encodeURIComponent(cursor)}` : ''}`,
				);
				if (generation !== this.generation) return;
				runInAction(() => {
					const known = new Set(this.items.map((item) => item.id));
					this.items = [...this.items, ...response.body.items.filter((item) => !known.has(item.id))].sort(newestFirst);
					this.clearedCursor = response.body.next_cursor;
				});
			}
		} catch {
			// The Cleared tab offers Show more again; nothing else depends on these.
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.clearedLoading = false;
				});
		}
	}
	private resumeSkippedLoad(): void {
		if (this.stale && this.consumers > 0 && this.retryTimer === null) void this.refresh();
	}
	private scheduleRetry(): void {
		const delay = RETRY_DELAYS_MS[Math.min(this.failures - 1, RETRY_DELAYS_MS.length - 1)]!;
		this.retryTimer = setTimeout(() => {
			this.retryTimer = null;
			void this.refresh();
		}, delay);
	}
	private clearRetry(): void {
		if (this.retryTimer !== null) clearTimeout(this.retryTimer);
		this.retryTimer = null;
	}
	/** One request for the whole inbox; the server clears every remaining item. */
	async clearAll(): Promise<boolean> {
		if (this.clearingAll || this.activeItems.length === 0) return false;
		const generation = this.generation;
		this.revision += 1;
		this.clearingAll = true;
		this.mutationError = false;
		try {
			await http.post(Endpoints.KONVO_CLICKUP_INBOX_CLEAR_ALL, {body: {}});
			if (generation !== this.generation) return false;
			runInAction(() => {
				this.items = this.items.map((item) => (item.is_cleared ? item : {...item, is_read: true, is_cleared: true}));
			});
			return true;
		} catch {
			if (generation === this.generation)
				runInAction(() => {
					this.mutationError = true;
				});
			return false;
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.clearingAll = false;
					this.revision += 1;
				});
			this.resumeSkippedLoad();
		}
	}
	async update(id: string, patch: {is_read?: boolean; is_cleared?: boolean}): Promise<boolean> {
		if (this.pending.has(id)) return false;
		const generation = this.generation;
		this.revision += 1;
		this.pending.add(id);
		this.mutationError = false;
		try {
			const response = await http.patch<ClickUpNotification>(
				`${Endpoints.KONVO_CLICKUP_INBOX}/${encodeURIComponent(id)}`,
				{body: patch},
			);
			if (generation !== this.generation) return false;
			runInAction(() => {
				this.items = this.items.map((item) => (item.id === id ? response.body : item));
			});
			return true;
		} catch {
			if (generation === this.generation)
				runInAction(() => {
					this.mutationError = true;
				});
			return false;
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.pending.delete(id);
					this.revision += 1;
				});
			this.resumeSkippedLoad();
		}
	}
}
export default new KonvoClickUpInboxStore();
